import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Image, 
  Layers, 
  User, 
  LogOut, 
  Plus,
  Trash2,
  Edit,
  X,
  BookOpen,
  Upload,
  Crop,
  ShoppingBag
} from 'lucide-react';
import { getApiUrl, makeRequest, resolveImageUrl } from '../../utils/api';
import ImageCropper from '../../components/ImageCropper';
import '../../styles/admin.css';

const AdminServices = () => {
  const { token, logout } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropExistingImage, setCropExistingImage] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    order: 0
  });

  const fetchServices = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('GET', `${apiUrl}/api/services`);
      if (result.ok) setServices(result.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('POST', `${apiUrl}/api/upload/image`, formDataUpload, {
        'Authorization': `Bearer ${token}`
      }, true);

      if (result.ok) {
        setFormData(prev => ({ ...prev, image: result.data.url }));
      } else {
        alert('Error uploading image: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image: ' + error.message);
    }
    setUploading(false);
  };

  const handleCroppedImageUpload = async (croppedBlob) => {
    setUploading(true);
    setShowCropper(false);
    setCropExistingImage(null);
    
    const uploadData = new FormData();
    uploadData.append('file', croppedBlob, 'cropped-image.jpg');

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('POST', `${apiUrl}/api/upload/image`, uploadData, {
        'Authorization': `Bearer ${token}`
      }, true);

      if (result.ok) {
        setFormData(prev => ({ ...prev, image: result.data.url }));
      } else {
        alert('Error uploading image: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image: ' + error.message);
    }
    setUploading(false);
  };

  const handleCropExisting = () => {
    setCropExistingImage(resolveImageUrl(formData.image));
    setShowCropper(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const apiUrl = getApiUrl();
    const url = editingService 
      ? `${apiUrl}/api/services/${editingService.id}`
      : `${apiUrl}/api/services`;
    
    const method = editingService ? 'PUT' : 'POST';

    try {
      const result = await makeRequest(method, url, formData, {
        'Authorization': `Bearer ${token}`
      });

      if (result.ok) {
        setShowModal(false);
        setEditingService(null);
        setFormData({ name: '', description: '', image: '', order: 0 });
        fetchServices();
      } else {
        alert('Error saving service: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving service: ' + error.message);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      image: service.image || '',
      order: service.order
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service? This will also affect portfolio items in this category.')) return;

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('DELETE', `${apiUrl}/api/services/${id}`, null, {
        'Authorization': `Bearer ${token}`
      });

      if (result.ok) {
        fetchServices();
      } else {
        alert('Error deleting service: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error deleting service: ' + error.message);
    }
  };

  const openAddModal = () => {
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      image: '',
      order: services.length
    });
    setShowModal(true);
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>The Slay Station</h2>
          <p>Admin Panel</p>
        </div>

        <nav className="admin-nav">
          <Link to="/admin/dashboard" className="admin-nav-item">
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/portfolio" className="admin-nav-item">
            <Image size={20} />
            <span>Portfolio</span>
          </Link>
          <Link to="/admin/services" className="admin-nav-item active">
            <Layers size={20} />
            <span>Services</span>
          </Link>
          <Link to="/admin/storefront" className="admin-nav-item">
            <ShoppingBag size={20} />
            <span>Curated Favorites</span>
          </Link>
          <Link to="/admin/business" className="admin-nav-item">
            <User size={20} />
            <span>Bio & Info</span>
          </Link>
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item" target="_blank">
            <BookOpen size={20} />
            <span>View Site</span>
          </Link>
          <button onClick={logout} className="admin-nav-item logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <div>
            <h1>Services</h1>
            <p>Manage your service categories</p>
          </div>
          <button onClick={openAddModal} className="admin-btn-primary">
            <Plus size={20} />
            Add Service
          </button>
        </header>

        <div className="admin-content">
          {loading ? (
            <div className="admin-loading">Loading...</div>
          ) : services.length === 0 ? (
            <div className="admin-empty">
              <Layers size={48} />
              <h3>No services yet</h3>
              <p>Add your first service category</p>
              <button onClick={openAddModal} className="admin-btn-primary">
                <Plus size={20} />
                Add First Service
              </button>
            </div>
          ) : (
            <div className="admin-services-grid">
              {services.map((service) => (
                <div key={service.id} className="admin-service-card">
                  <div className="admin-service-info">
                    <h3>{service.name}</h3>
                    <p>{service.description}</p>
                  </div>
                  <div className="admin-service-actions">
                    <button onClick={() => handleEdit(service)} className="admin-btn-icon">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => handleDelete(service.id)} className="admin-btn-icon delete">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingService ? 'Edit Service' : 'Add Service'}</h2>
              <button onClick={() => setShowModal(false)} className="admin-modal-close">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>Service Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Facials"
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this service category..."
                  rows={3}
                />
              </div>

              <div className="admin-form-group">
                <label>Service Image (Optional)</label>
                <div className="admin-image-upload">
                  {formData.image ? (
                    <div className="admin-image-preview">
                      <img src={resolveImageUrl(formData.image)} alt="Preview" />
                      <div className="admin-image-actions">
                        <button 
                          type="button" 
                          onClick={handleCropExisting}
                          className="admin-image-crop"
                          title="Crop Image"
                        >
                          <Crop size={16} />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                          className="admin-image-remove"
                          title="Remove Image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-upload-options-compact">
                      <button
                        type="button"
                        onClick={() => {
                          setCropExistingImage(null);
                          setShowCropper(true);
                        }}
                        className="admin-crop-upload-btn-small"
                        disabled={uploading}
                      >
                        <Crop size={18} />
                        <span>Crop & Upload</span>
                      </button>
                      <label className="admin-upload-btn-small">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          hidden
                        />
                        {uploading ? (
                          <span>Uploading...</span>
                        ) : (
                          <>
                            <Upload size={18} />
                            <span>Quick Upload</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
                <p className="admin-form-hint">Or paste an image URL:</p>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="admin-form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="admin-btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="admin-btn-primary">
                  {editingService ? 'Save Changes' : 'Add Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && (
        <ImageCropper
          onCropComplete={handleCroppedImageUpload}
          onCancel={() => {
            setShowCropper(false);
            setCropExistingImage(null);
          }}
          existingImageUrl={cropExistingImage}
          parentToken={token}
        />
      )}
    </div>
  );
};

export default AdminServices;
